import { memo } from "react";
import type { CampusEnergySnapshot, BuildingEnergyReading } from "../lib/energyModel";
import type { CampusForecast, FallbackTier } from "@kmitl/shared";
import { ForecastSparkline } from "./ForecastSparkline";
import { PanelHeader } from "./PanelHeader";

/**
 * EnergyPanel — campus aggregate view that pairs with the heatmap.
 *
 * Shows total live draw (kW), 24h kWh, solar offset, net grid import,
 * top consumers, and the anomaly list. Reads from the same readings the
 * map colors with, so panel + map can never disagree.
 *
 * Tags every number as "synthetic" until BEMS is wired — the numerator
 * is the partnership story leadership cares about.
 */

interface Props {
  snapshot: CampusEnergySnapshot;
  onSelectBuilding?: (b: BuildingEnergyReading) => void;
  forecast?: CampusForecast | null;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

function fmtKw(kw: number): string {
  if (kw >= 1000) return `${(kw / 1000).toFixed(2)} MW`;
  return `${Math.round(kw)} kW`;
}
function fmtKwh(kwh: number): string {
  if (kwh >= 1_000_000) return `${(kwh / 1_000_000).toFixed(2)} GWh`;
  if (kwh >= 1000) return `${(kwh / 1000).toFixed(1)} MWh`;
  return `${Math.round(kwh)} kWh`;
}
function flagColor(flag: string): string {
  if (flag === "stuck-cooling") return "var(--bad)";
  if (flag === "possible-outage") return "var(--bad)";
  if (flag === "elevated") return "var(--warn)";
  if (flag === "after-hours-load") return "var(--warn)";
  return "var(--data)";
}

function fmtTons(kg: number): string {
  if (Math.abs(kg) >= 1000) return `${(kg / 1000).toFixed(1)} t`;
  return `${Math.round(kg)} kg`;
}

export const EnergyPanel = memo(function EnergyPanel({ snapshot, onSelectBuilding, forecast, ageMinutes, fallbackTier }: Props) {
  const {
    totalKw, totalDailyKwh, buildingCount, anomalyCount, topConsumers, anomalies,
    solarKw, netGridKw,
    carbonGridKgPerDay, carbonSolarAvoidedKgPerDay, carbonTreesAbsorbedKgPerDay,
    carbonNetKgPerDay, carbonNeutralityPct,
  } = snapshot;
  const solarPct = totalKw > 0 ? Math.round((solarKw / totalKw) * 100) : 0;
  const neutralColor =
    carbonNeutralityPct >= 100 ? "var(--good)" :
    carbonNeutralityPct >= 50  ? "var(--warn)" :
                                  "var(--bad)";
  return (
    <div className="energy-panel">
      <header className="energy-panel-head">
        <span className="lcars-bar" />
        <PanelHeader title="ENERGY · CAMPUS" ageMinutes={ageMinutes} source="bma-ckan" fallbackTier={fallbackTier} />
        <span className="mono caption">SYNTHETIC · stand-in for BEMS</span>
      </header>

      <div className="energy-bignum">
        <div className="energy-stat">
          <span className="energy-stat-lbl mono">DRAW NOW</span>
          <span className="energy-stat-val mono">{fmtKw(totalKw)}</span>
          <span className="energy-stat-sub mono">across {buildingCount} buildings</span>
        </div>
        <div className="energy-stat">
          <span className="energy-stat-lbl mono">SOLAR PV</span>
          <span className="energy-stat-val mono" style={{ color: "var(--good)" }}>{fmtKw(solarKw)}</span>
          <span className="energy-stat-sub mono">{solarPct}% of load · CU-MEA microgrid</span>
        </div>
        <div className="energy-stat">
          <span className="energy-stat-lbl mono">NET GRID</span>
          <span className="energy-stat-val mono" style={{ color: netGridKw > 0 ? "var(--warn)" : "var(--good)" }}>
            {netGridKw > 0 ? "+" : ""}{fmtKw(netGridKw)}
          </span>
          <span className="energy-stat-sub mono">{netGridKw > 0 ? "import" : "export"}</span>
        </div>
        <div className="energy-stat">
          <span className="energy-stat-lbl mono">24H</span>
          <span className="energy-stat-val mono">{fmtKwh(totalDailyKwh)}</span>
          <span className="energy-stat-sub mono">projected · ~{Math.round(totalDailyKwh * 4 / 1000)} ฿k @ ฿4/kWh</span>
        </div>
      </div>

      <section className="energy-section">
        <header className="energy-section-head">
          <span className="dot" style={{ background: neutralColor }} />
          <span className="eyebrow mono">Carbon ledger · today</span>
          <span className="mono caption" style={{ color: neutralColor }}>
            {carbonNeutralityPct}% to net-zero
          </span>
        </header>
        <div className="energy-carbon-grid">
          <div className="energy-carbon-cell" style={{ borderLeftColor: "var(--bad)" }}>
            <span className="energy-stat-lbl mono">GRID EMISSIONS</span>
            <span className="energy-stat-val mono">{fmtTons(carbonGridKgPerDay)} CO₂</span>
            <span className="energy-stat-sub mono">EGAT factor 0.428 kg/kWh</span>
          </div>
          <div className="energy-carbon-cell" style={{ borderLeftColor: "var(--good)" }}>
            <span className="energy-stat-lbl mono">SOLAR AVOIDED</span>
            <span className="energy-stat-val mono">−{fmtTons(carbonSolarAvoidedKgPerDay)} CO₂</span>
            <span className="energy-stat-sub mono">5.4 MW PV × 5 sun-h/day</span>
          </div>
          <div className="energy-carbon-cell" style={{ borderLeftColor: "var(--good)" }}>
            <span className="energy-stat-lbl mono">TREES ABSORB</span>
            <span className="energy-stat-val mono">−{fmtTons(carbonTreesAbsorbedKgPerDay)} CO₂</span>
            <span className="energy-stat-sub mono">campus trees @ 30 kg/yr each</span>
          </div>
          <div className="energy-carbon-cell" style={{ borderLeftColor: neutralColor }}>
            <span className="energy-stat-lbl mono">NET TODAY</span>
            <span className="energy-stat-val mono" style={{ color: neutralColor }}>
              {carbonNetKgPerDay > 0 ? "+" : ""}{fmtTons(carbonNetKgPerDay)} CO₂
            </span>
            <span className="energy-stat-sub mono">
              {carbonNetKgPerDay > 0 ? `gap to neutral` : `surplus over neutral`}
            </span>
          </div>
        </div>
      </section>

      {anomalies.length > 0 && (
        <section className="energy-section">
          <header className="energy-section-head">
            <span className="dot unavailable" />
            <span className="eyebrow mono" style={{ color: "var(--bad)" }}>
              {anomalyCount} anomal{anomalyCount === 1 ? "y" : "ies"} flagged
            </span>
          </header>
          <ul className="energy-list">
            {anomalies.map((b) => (
              <li
                key={b.buildingId}
                className="energy-row energy-row-anomaly"
                onClick={() => onSelectBuilding?.(b)}
              >
                <span className="energy-row-name">{b.name ?? b.buildingId}</span>
                <span className="energy-row-kw mono">{fmtKw(b.currentKw)}</span>
                <span className="energy-row-vs mono">×{b.vsAvg.toFixed(1)}</span>
                <span className="energy-row-flags mono">
                  {b.flags.map((f) => (
                    <span key={f} style={{ color: flagColor(f) }}>{f}</span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="energy-section">
        <header className="energy-section-head">
          <span className="dot live" />
          <span className="eyebrow mono">Top consumers · live</span>
        </header>
        <ul className="energy-list">
          {topConsumers.map((b) => (
            <li
              key={b.buildingId}
              className="energy-row"
              onClick={() => onSelectBuilding?.(b)}
            >
              <span className="energy-row-name">{b.name ?? b.buildingId}</span>
              <span className="energy-row-kw mono">{fmtKw(b.currentKw)}</span>
              <span className="energy-row-vs mono">{b.buildingType}</span>
              <span className="energy-row-area mono">{(b.floorAreaM2 / 1000).toFixed(1)}km²</span>
            </li>
          ))}
        </ul>
      </section>

      {forecast?.energy && (
        <section className="energy-section">
          <header className="energy-section-head">
            <span className="dot" style={{ background: "var(--data)" }} />
            <span className="eyebrow mono">TimeFM · 24h demand forecast</span>
            <span className="mono caption" style={{ color: forecast.energy.model === "timefm" ? "var(--data)" : "var(--text-3)" }}>
              {forecast.energy.model === "timefm" ? "VERTEX AI" : forecast.energy.model.toUpperCase()}
            </span>
          </header>
          <ForecastSparkline series={forecast.energy} height={56} accentColor="var(--warn)" showLabels />
        </section>
      )}
    </div>
  );
});
